// @ts-check

/**
 * @typedef {import('./utils/transformPadPrescriptionsToPrescriptionLoadSchedule').PadVisit} PadVisit
 * @typedef {import('./utils/buildPadPrescriptionEvent').PadPrescription} PadPrescription
 * @typedef {import('./utils/buildPadPrescriptionEvent').PadPrescriptionEvent} PadPrescriptionEvent
 * @typedef {import('./utils/buildPadPrescriptionEvent').DtEvent} DtEvent
 *
 */

/**
 * @typedef {Object} PrescriptionLoadSchedule
 * @property {string} key - Chave identificadora (ex: "g120")
 * @property {string} date - Data da visita no formato DD/MM/AAAA (ex: "26/08/2026")
 * @property {string} time - Horário da visita (ex: "09:00")
 * @property {Array<number>} padPrescriptionIds - Lista de IDs de prescrição
 * @property {boolean} isEnabledForSelection - Indica se está habilitado para seleção
 * @property {Array<PadPrescriptionEvent>} padPrescriptionEvents - Lista de eventos de prescrição
 * @property {string} uniqueVisitKey - Chave única combinando data e hora (ex: "26-08-2026-0900")
 */

/**
 * @typedef {Object} VisitScheduleBaseSchema
 * @property {number|null} id
 * @property {number|null} padVisitId
 * @property {string|null} shiftStartTime
 * @property {PadVisit|null} padVisit
 * @property {Array<Event>} previousEvents
 * @property {Array<Event>} events
 * @property {boolean} automaticRepeat
 * @property {Array<Object>} prescriptionEventsSchedule
 * @property {Array<PrescriptionLoadSchedule>} prescriptionLoadSchedule
 * @property {boolean} dailyAutomaticRepeat
 * @property {boolean} canStepFoward
 * @property {string} status
 * @property {any} error
 */

/**
 * @typedef {VisitScheduleBaseSchema} CalendarVisitSchedule
 */

/**
 * @typedef {VisitScheduleBaseSchema} VisitPrescriptionSchedule
 */

import STATUS from 'app/redux/features/pad-initialization/constants/status.constant';

/**
 * Generates a random code consisting of a letter followed by three digits.
 * @returns {string} Randomly generated code.
 */
export function generateCode() {
  const alpha = 'abcdefghijklmnopqrstuvwxyz';
  const digit = () => Math.floor(Math.random() * 10);
  const firstLetter = alpha[Math.floor(Math.random() * alpha.length)];
  return `${firstLetter}${digit()}${digit()}${digit()}`;
}

/**
 * @typedef {Object} Event
 * @property {string} key
 * @property {number|null} professionalId
 * @property {Object} professional
 * @property {string|null} dateTime
 * @property {Array<number>} padPrescriptionIds
 * @property {Array<PadPrescriptionEvent>} padPrescriptionEvents
 */

/**
 * Default structure for an Event.
 * @type {Event}
 */
export const DEFAULT_EVENT = {
  key: generateCode(),
  professionalId: null,
  professional: {},
  dateTime: null,
  padPrescriptionIds: [],
  padPrescriptionEvents: []
};

/**
 * Factory function that creates an Event instance.
 * Ensures all event objects have the correct structure.
 *
 * @param {Partial<Event>} data - Partial event data to override defaults.
 * @returns {Event} A new Event instance.
 */
export const createEvent = data => {
  if (!data) {
    return { ...DEFAULT_EVENT };
  }

  return {
    key: data.key || generateCode(),
    professionalId: data.professionalId || null,
    professional: data.professional || {},
    dateTime: data.dateTime || null,
    padPrescriptionIds: data.padPrescriptionIds || [],
    padPrescriptionEvents: data.padPrescriptionEvents || []
  };
};

/**
 * Validates an event object structure.
 *
 * @param {Event} event - Event to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidEvent = event => {
  if (!event || typeof event !== 'object') {
    return false;
  }

  const hasProfessionalId = event.professionalId !== undefined && event.professionalId !== null;
  const hasDateTime = event.dateTime !== undefined && event.dateTime !== null;
  const hasProfessional = event.professional !== undefined;

  return hasProfessionalId && hasDateTime && hasProfessional;
};

/**
 * Default structure for a VisitPrescriptionSchedule.
 * Centralizes the schema definition to ensure consistency across the application.
 */
const DEFAULT_VISIT_SCHEDULE = {
  id: null,
  padVisitId: null,
  shiftStartTime: null,
  padVisit: null,
  previousEvents: [],
  events: [],
  automaticRepeat: false,
  prescriptionEventsSchedule: [],
  prescriptionLoadSchedule: [],
  dailyAutomaticRepeat: false,
  canStepFoward: false,
  status: STATUS.IDLE,
  error: null
};

/**
 * Cria uma instância de CalendarVisitSchedule usando apenas padVisit
 * @param {PadVisit} padVisit - Objeto padVisit utilizado para construir o schedule
 * @returns {CalendarVisitSchedule} CalendarVisitSchedule
 */
export const buildCalendarVisitSchedule = padVisit => {
  if (!padVisit) {
    return { ...DEFAULT_VISIT_SCHEDULE };
  }

  return {
    ...DEFAULT_VISIT_SCHEDULE,
    id: padVisit.id,
    padVisitId: padVisit.id,
    padVisit,
    shiftStartTime: padVisit.shiftStartTime
  };
};

/**
 * Cria uma instância de VisitPrescriptionSchedule usando apenas padVisit
 * @param {PadVisit} padVisit - Objeto padVisit utilizado para construir o schedule
 * @returns {VisitPrescriptionSchedule} VisitPrescriptionSchedule
 */
export const buildVisitPrescriptionSchedule = padVisit => {
  if (!padVisit) {
    return { ...DEFAULT_VISIT_SCHEDULE };
  }

  return {
    ...DEFAULT_VISIT_SCHEDULE,
    id: padVisit.id,
    padVisitId: padVisit.id,
    padVisit
  };
};

/**
 * Cria uma instância de CalendarVisitSchedule a partir de um objeto de schedule.
 *
 * @param {CalendarVisitSchedule} calendarVisitSchedule - Objeto de schedule contendo dados para construir o CalendarVisitSchedule.
 * @returns {CalendarVisitSchedule} CalendarVisitSchedule
 */
export const createCalendarVisitSchedule = calendarVisitSchedule => {
  if (!calendarVisitSchedule) {
    return { ...DEFAULT_VISIT_SCHEDULE };
  }

  const { padVisit, events } = calendarVisitSchedule;
  const padVisitId = calendarVisitSchedule.id || null;
  const shiftStartTime = calendarVisitSchedule.shiftStartTime || null;
  const id = padVisitId;

  return {
    id,
    padVisitId,
    shiftStartTime,
    padVisit,
    previousEvents: calendarVisitSchedule.previousEvents || [],
    events: Array.isArray(events) ? calendarVisitSchedule.events.map(createEvent) : [],
    prescriptionEventsSchedule: [],
    prescriptionLoadSchedule: calendarVisitSchedule.prescriptionLoadSchedule || [],
    automaticRepeat: calendarVisitSchedule.automaticRepeat || false,
    dailyAutomaticRepeat: calendarVisitSchedule.dailyAutomaticRepeat || false,
    canStepFoward: false,
    status: STATUS.IDLE,
    error: null
  };
};

/**
 * Cria uma instância de VisitPrescriptionSchedule a partir de prescription schedule
 *
 * @param {VisitPrescriptionSchedule} prescriptionSchedule - O prescription schedule.
 * @returns {VisitPrescriptionSchedule} VisitPrescriptionSchedule
 */
export const createVisitPrescriptionSchedule = prescriptionSchedule => {
  if (!prescriptionSchedule) {
    return { ...DEFAULT_VISIT_SCHEDULE };
  }

  const { padVisit, events } = prescriptionSchedule;
  const padVisitId = prescriptionSchedule.id || null;
  const shiftStartTime = prescriptionSchedule.shiftStartTime || null;
  const id = padVisitId;

  return {
    id,
    padVisitId,
    shiftStartTime,
    padVisit,
    previousEvents: [],
    events: Array.isArray(events) ? prescriptionSchedule.events.map(createEvent) : [],
    prescriptionEventsSchedule: [],
    prescriptionLoadSchedule: prescriptionSchedule.prescriptionLoadSchedule || [],
    automaticRepeat: prescriptionSchedule.automaticRepeat || false,
    dailyAutomaticRepeat: prescriptionSchedule.dailyAutomaticRepeat || false,
    canStepFoward: false,
    status: STATUS.IDLE,
    error: null
  };
};
